import { useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogIn, LogOut, Clock, History, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const fmtTime = (ts) => { try { return new Date(ts).toLocaleTimeString("sk-SK", { hour: "2-digit", minute: "2-digit" }); } catch { return ts; } };

const WD = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"];

function MonthCalendar({ month, daily, today, selected, onSelect }) {
  const [y, m] = month.split("-").map(Number);
  const byDate = {};
  (daily || []).forEach((d) => { byDate[d.date] = d; });
  const firstDow = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const days = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return (
    <div data-testid="attendance-calendar">
      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-muted-foreground">
        {WD.map((w) => <div key={w}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const ds = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const rec = byDate[ds];
          const isToday = ds === today;
          const miss = rec && rec.missing_out > 0;
          const has = rec && rec.records?.length > 0;
          const isSel = ds === selected;
          return (
            <button key={i} type="button" onClick={() => has && onSelect(isSel ? null : ds)} disabled={!has}
              data-testid={`attendance-cal-day-${ds}`}
              className={`flex aspect-square flex-col items-center justify-center rounded-md border transition-colors
                ${miss ? "border-amber-500/60 bg-amber-500/15" : has ? "border-border bg-background/60" : "border-transparent bg-background/20"}
                ${isToday ? "ring-2 ring-primary" : ""} ${isSel ? "border-primary" : ""}
                ${has ? "cursor-pointer hover:border-primary/70" : "cursor-default opacity-50"}`}>
              <span className={`text-[11px] font-semibold ${miss ? "text-amber-400" : "text-foreground"}`}>{d}</span>
              {has && <span className={`font-mono text-[10px] leading-tight ${miss ? "text-amber-400" : "text-primary"}`}>{rec.hours} h</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Attendance() {
  const { token } = useParams();
  const now = new Date();
  const [pin, setPin] = useState("");
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const [hmonth, setHmonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [selectedDay, setSelectedDay] = useState(null);

  const load = async (p) => {
    setBusy(true);
    try { const { data } = await api.post("/public/attendance/state", { token, pin: p ?? pin }); setState(data); setAuthed(true); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setAuthed(false); }
    finally { setBusy(false); }
  };
  const punch = async () => {
    setBusy(true);
    try { const { data } = await api.post("/public/attendance/punch", { token, pin }); setState(data); if (histOpen) loadHist(hmonth); toast.success(data.punched === "in" ? "Príchod zapísaný" : "Odchod zapísaný"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const loadHist = async (m) => {
    setHist(null); setSelectedDay(null);
    try { const { data } = await api.post("/public/attendance/history", { token, pin, month: m }); setHist(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleHist = () => { const nv = !histOpen; setHistOpen(nv); if (nv) loadHist(hmonth); };

  return (
    <div className="min-h-screen bg-background text-foreground" data-theme="gold">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center p-5">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-lg bg-primary text-primary-foreground"><Clock className="h-6 w-6" /></div>
          <h1 className="font-heading text-2xl font-extrabold uppercase">Dochádzka</h1>
          <p className="text-sm text-muted-foreground">APIS Manager</p>
        </div>

        {!authed ? (
          <div className="card-surface space-y-4 p-6">
            <div className="space-y-1.5"><Label>Zadajte svoj PIN</Label><Input inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="••••" className="text-center text-2xl tracking-[0.5em]" data-testid="attendance-pin" onKeyDown={(e) => { if (e.key === "Enter" && pin.length === 4) load(); }} /></div>
            <Button className="w-full" disabled={busy || pin.length !== 4} onClick={() => load()} data-testid="attendance-login">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pokračovať"}</Button>
          </div>
        ) : (
          <div className="card-surface space-y-5 p-6" data-testid="attendance-panel">
            <div className="text-center"><div className="text-sm text-muted-foreground">Prihlásený</div><div className="font-heading text-xl font-bold">{state.name}</div></div>
            <Button className={`h-20 w-full text-lg font-bold ${state.next_action === "in" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}`} disabled={busy} onClick={punch} data-testid="attendance-punch">
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : state.next_action === "in" ? <><LogIn className="mr-2 h-6 w-6" /> Zapísať príchod</> : <><LogOut className="mr-2 h-6 w-6" /> Zapísať odchod</>}
            </Button>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg border border-border bg-background/40 p-3"><div className="text-xs text-muted-foreground">Dnes</div><div className="font-mono text-lg font-bold" data-testid="attendance-today">{state.today_hours} h</div></div>
              <div className="rounded-lg border border-border bg-background/40 p-3"><div className="text-xs text-muted-foreground">Týždeň</div><div className="font-mono text-lg font-bold">{state.week_hours} h</div></div>
              <div className="rounded-lg border border-border bg-background/40 p-3"><div className="text-xs text-muted-foreground">Mesiac</div><div className="font-mono text-lg font-bold">{state.month_hours} h</div></div>
            </div>
            {state.today_records?.length > 0 && (
              <div className="space-y-1 text-sm" data-testid="attendance-today-records">
                <div className="font-semibold">Dnešné záznamy</div>
                {state.today_records.map((r, i) => <div key={i} className="flex justify-between border-b border-border/40 py-1"><span>{r.type === "in" ? "Príchod" : "Odchod"}</span><span className="font-mono">{fmtTime(r.ts)}</span></div>)}
              </div>
            )}
            <div className="border-t border-border/50 pt-4">
              <button onClick={toggleHist} className="flex w-full items-center justify-between text-sm font-semibold" data-testid="attendance-history-toggle">
                <span className="flex items-center gap-2"><History className="h-4 w-4" /> Moja história dochádzky</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${histOpen ? "rotate-180" : ""}`} />
              </button>
              {histOpen && (
                <div className="mt-3 space-y-3" data-testid="attendance-history">
                  <Input type="month" value={hmonth} max={`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`} onChange={(e) => { setHmonth(e.target.value); loadHist(e.target.value); }} className="w-full" data-testid="attendance-history-month" />
                  {!hist ? <div className="py-4 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" /></div> :
                    !hist.daily.length ? <div className="py-4 text-center text-sm text-muted-foreground">Žiadne záznamy za tento mesiac.</div> : (
                      <>
                        <div className="flex justify-between text-sm"><span className="text-muted-foreground">Spolu za mesiac</span><span className="font-mono font-bold text-primary">{hist.month_total} h</span></div>
                        <MonthCalendar month={hmonth} daily={hist.daily} today={`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`} selected={selectedDay} onSelect={setSelectedDay} />
                        {selectedDay && (() => {
                          const d = hist.daily.find((x) => x.date === selectedDay);
                          if (!d) return null;
                          return (
                            <div className="rounded-lg border border-primary/40 bg-background/50 p-3" data-testid="attendance-day-detail">
                              <div className="mb-2 flex items-center justify-between text-sm font-semibold">
                                <span className="flex items-center gap-2">{d.date}{d.missing_out > 0 && <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-400">chýba odchod</span>}</span>
                                <span className="font-mono">{d.hours} h</span>
                              </div>
                              <div className="space-y-1 text-sm">
                                {d.records.map((r, i) => <div key={i} className="flex justify-between border-b border-border/40 py-1"><span>{r.type === "in" ? "Príchod" : "Odchod"}</span><span className="font-mono">{fmtTime(r.ts)}</span></div>)}
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    )}
                </div>
              )}
            </div>
            <Button variant="ghost" className="w-full" onClick={() => { setAuthed(false); setPin(""); setState(null); setHistOpen(false); setHist(null); setSelectedDay(null); }}>Odhlásiť</Button>
          </div>
        )}
      </div>
    </div>
  );
}
